import { useState, useEffect } from 'react';
import AdminShell from '../../components/admin/AdminShell';
import { useToast } from '../../context/ToastContext';
import { adminAPI } from '../../utils/api';

const SLOT_INFO = {
  A: { name: 'Dashboard Banner', location: 'Top of student dashboard — every login', size: '1200 × 90px' },
  B: { name: 'Study Screen Sidebar', location: 'Sidebar while studying a topic', size: '300 × 250px' },
  C: { name: 'Quiz Results Screen', location: 'After completing a quiz', size: '728 × 90px' }
};

const AdminBannersPage = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ slot: 'A', title: '', image_url: '', destination_url: '', target_class: '', start_date: '', end_date: '' });
  const { success, error } = useToast();

  useEffect(() => { loadBanners(); }, []);

  const loadBanners = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getBanners();
      setBanners(res.data.data || []);
    } catch { setBanners([]); }
    finally { setLoading(false); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title || !form.image_url || !form.destination_url) return error('Title, image URL and destination URL are required.');
    try {
      await adminAPI.createBanner({ ...form, target_class: form.target_class || null });
      success('Banner created and scheduled!');
      setShowForm(false);
      setForm({ slot: 'A', title: '', image_url: '', destination_url: '', target_class: '', start_date: '', end_date: '' });
      loadBanners();
    } catch { error('Failed to create banner.'); }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Remove banner "${title}"?`)) return;
    try { await adminAPI.deleteBanner(id); success('Banner removed.'); loadBanners(); }
    catch { error('Failed to remove banner.'); }
  };

  const getBannerBySlot = (slot) => banners.find(b => b.slot === slot);

  const isActive = (b) => {
    if (!b || !b.is_active || !b.image_url) return false;
    const today = new Date().toISOString().split('T')[0];
    if (b.start_date && b.start_date > today) return false;
    if (b.end_date && b.end_date < today) return false;
    return true;
  };

  return (
    <AdminShell
      title="Banner Slot Management"
      subtitle="Banners only show when a creative is uploaded. Empty slots are invisible to students."
      actions={
        <button onClick={() => setShowForm(true)} style={{ background: '#FF6B00', color: 'white', border: 'none', borderRadius: '8px', padding: '7px 14px', fontSize: '12px', fontWeight: 800, cursor: 'pointer' }}>
          + Create Banner
        </button>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

        {/* Important note */}
        <div style={{ background: '#E8F0FB', border: '1px solid #1A3C6E', borderRadius: '10px', padding: '12px 16px', fontSize: '12px', color: '#1A3C6E', fontWeight: 600 }}>
          📌 <strong>Critical Rule:</strong> A banner slot renders NOTHING if no creative is uploaded. No placeholder, no empty space — the layout adapts automatically. Only education-relevant brands permitted.
        </div>

        {/* Slot cards */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" style={{ margin: '0 auto' }}></div></div>
        ) : (
          ['A', 'B', 'C'].map(slot => {
            const banner = getBannerBySlot(slot);
            const active = isActive(banner);
            const info = SLOT_INFO[slot];

            return (
              <div key={slot} style={{ background: 'white', border: `1px solid ${active ? '#1B7F3E' : '#E2E8F0'}`, borderRadius: '14px', padding: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                  <div style={{ flexShrink: 0 }}>
                    <span style={{ fontSize: '12px', fontWeight: 800, background: '#E8F0FB', color: '#1A3C6E', borderRadius: '20px', padding: '3px 10px' }}>Slot {slot}</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: 800, marginBottom: '3px' }}>{info.name}</div>
                    <div style={{ fontSize: '11px', color: '#64748B', marginBottom: '2px' }}>{info.location}</div>
                    <div style={{ fontSize: '10px', color: '#94A3B8' }}>Recommended size: {info.size}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: active ? '#1B7F3E' : '#64748B' }}>
                      {active ? '● Live' : '○ Empty'}
                    </span>
                  </div>
                </div>

                {banner ? (
                  <div style={{ marginTop: '12px', padding: '12px', background: '#F7F8FC', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 700 }}>{banner.title}</div>
                        <div style={{ fontSize: '11px', color: '#64748B', marginTop: '2px' }}>
                          {banner.start_date && `From: ${banner.start_date}`} {banner.end_date && `· Until: ${banner.end_date}`}
                          {banner.target_class && ` · Class ${banner.target_class} only`}
                        </div>
                        <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '4px' }}>
                          👁️ {banner.impressions} impressions · 👆 {banner.clicks} clicks
                          {banner.impressions > 0 && ` · CTR: ${((banner.clicks / banner.impressions) * 100).toFixed(1)}%`}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <a href={banner.image_url} target="_blank" rel="noopener noreferrer" style={{ background: '#E8F0FB', color: '#1A3C6E', borderRadius: '8px', padding: '5px 10px', fontSize: '11px', fontWeight: 700, textDecoration: 'none' }}>Preview</a>
                        <button onClick={() => handleDelete(banner.id, banner.title)} style={{ background: '#FEE2E2', color: '#DC2626', border: 'none', borderRadius: '8px', padding: '5px 10px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>Remove</button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop: '12px', padding: '16px', border: '1.5px dashed #E2E8F0', borderRadius: '8px', textAlign: 'center', color: '#94A3B8', fontSize: '12px' }}>
                    No banner uploaded · This slot is invisible to students
                  </div>
                )}
              </div>
            );
          })
        )}

        {/* Create Banner Form */}
        {showForm && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px' }}>
            <div style={{ background: 'white', borderRadius: '16px', padding: '28px', maxWidth: '520px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 800 }}>Create Banner</h3>
                <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748B' }}>×</button>
              </div>
              <form onSubmit={handleCreate}>
                {[
                  { key: 'slot', label: 'Banner Slot', type: 'select', options: [['A', 'Slot A — Dashboard'], ['B', 'Slot B — Study Screen'], ['C', 'Slot C — Quiz Results']] },
                  { key: 'title', label: 'Internal Title (admin only)', type: 'text', placeholder: 'e.g. Classmate Notebooks — April 2026' },
                  { key: 'image_url', label: 'Creative Image URL *', type: 'url', placeholder: 'https://...' },
                  { key: 'destination_url', label: 'Click Destination URL *', type: 'url', placeholder: 'https://...' },
                ].map(field => (
                  <div key={field.key} style={{ marginBottom: '14px' }}>
                    <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '5px' }}>{field.label}</label>
                    {field.type === 'select' ? (
                      <select value={form[field.key]} onChange={e => setForm(p => ({ ...p, [field.key]: e.target.value }))}
                        style={{ width: '100%', background: '#F7F8FC', border: '1.5px solid #E2E8F0', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', outline: 'none' }}>
                        {field.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    ) : (
                      <input type={field.type} value={form[field.key]} onChange={e => setForm(p => ({ ...p, [field.key]: e.target.value }))} placeholder={field.placeholder}
                        style={{ width: '100%', background: '#F7F8FC', border: '1.5px solid #E2E8F0', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', outline: 'none' }} />
                    )}
                  </div>
                ))}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '14px' }}>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '5px' }}>Start Date</label>
                    <input type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))}
                      style={{ width: '100%', background: '#F7F8FC', border: '1.5px solid #E2E8F0', borderRadius: '8px', padding: '8px 10px', fontSize: '12px', outline: 'none' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '5px' }}>End Date</label>
                    <input type="date" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))}
                      style={{ width: '100%', background: '#F7F8FC', border: '1.5px solid #E2E8F0', borderRadius: '8px', padding: '8px 10px', fontSize: '12px', outline: 'none' }} />
                  </div>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.4px', display: 'block', marginBottom: '5px' }}>Target Class</label>
                    <select value={form.target_class} onChange={e => setForm(p => ({ ...p, target_class: e.target.value }))}
                      style={{ width: '100%', background: '#F7F8FC', border: '1.5px solid #E2E8F0', borderRadius: '8px', padding: '8px 10px', fontSize: '12px', outline: 'none' }}>
                      <option value="">All Classes</option>
                      {[6,7,8,9,10].map(c => <option key={c} value={c}>Class {c}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="button" onClick={() => setShowForm(false)} style={{ flex: 1, background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '11px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
                  <button type="submit" style={{ flex: 2, background: '#FF6B00', color: 'white', border: 'none', borderRadius: '8px', padding: '11px', fontSize: '13px', fontWeight: 800, cursor: 'pointer' }}>Create Banner</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminShell>
  );
};

export default AdminBannersPage;
